// @flow
import React, { useState, useCallback, useMemo } from 'react';
import { useSelector, shallowEqual } from 'react-redux';

import RowLayout, { Header } from 'app/common/layouts/RowLayout';
import Loading from 'app/common/components/Loader';
import EmptyWarn from 'app/common/layouts/EmptyWarn';
import Link from 'app/common/lib/link';
import getNumEnding from 'app/common/lib/getNumEnding';
import ConfirmationDialog from 'app/common/components/Confirmation/ConfirmationDialog';
import { CheckoutButton } from 'app/common/layouts/commonShop';

import { selectItemsNumber } from 'app/common/components/Shop';

import { useShopCartContext } from '../../CartInit';
import selectCartItems from './selectCartItems';
import CartItem from './CartItem';
import Summary from '../../Summary';
import styles from './OrderList.module.scss';;


/**
  Как работает корзина: id корзины на бэкэнде не привязан к пользователю, поэтому храним его в settings.
  <CartInit /> в App приложения достает этот id из settings и пытается получить корзину с содержимым по этому id.
  Там же делается сохранение этого id в settings, если изменился id в самой корзине
*/
type Props = {
  baseUrl: string,
}

const OrderList = ({ baseUrl }: Props) => {
  const loading = useSelector(state => state.shop.cart.loading, shallowEqual);
  const { total, amountDiscount } = useSelector(state => state.shop.cart, shallowEqual);
  const cartItems = useSelector(selectCartItems);
  const itemsNumber = useSelector(selectItemsNumber);


  const header = useMemo(() => {
    if (!itemsNumber) return 'Корзина';
    const itemsText = getNumEnding(itemsNumber, ['товар', 'товара', 'товаров']);
    return `Корзина (${itemsNumber} ${itemsText})`;
  }, [itemsNumber]);

  const [, cartActions] = useShopCartContext();

  const [clearingAll, setClearingAll] = useState(false);
  const handleClearAll = useCallback(async () => {
    setClearingAll(true);
    await cartActions.newCart();
    setClearingAll(false);
  }, [cartActions]);


  const updateCart = useCallback(() => {
    cartActions.cartRequest();
  }, [cartActions]);


  return (
    <React.Fragment>
      <RowLayout flex>
        <Header>{header}</Header>
        {itemsNumber > 0
          && <ConfirmationDialog
            onConfirm={handleClearAll}
            text="Удалить все товары из корзины"
            confirmButtonText="Удалить"
            confirmButtonProps={{ error: true }}
          >
            {handleConfirm => (<a href="clear" onClick={handleConfirm} className={styles.clearCart}>
              {'Очистить корзину'}
            </a>)}
          </ConfirmationDialog>}
      </RowLayout>

      <RowLayout flex>
        <div className={styles.items}>
          {loading && <Loading />}
          {!itemsNumber
            && !loading
            && <React.Fragment>
              <EmptyWarn className={styles.emptyWarn}>{'В корзине пока ничего нет'}</EmptyWarn>
              <p>
                {'Перейдите в '}
                <Link to="/shop/catalog" className={styles.link}>{'каталог'}</Link>
                {', для добавления товаров.'}
              </p>
            </React.Fragment>}
          {itemsNumber > 0
            && !loading
            && cartItems.map(({ id, variantId }) => (
              <CartItem
                key={id}
                productId={variantId}
                locked={clearingAll}
                onDeleteProduct={updateCart}
                deleteByCross
              />
            ))}
        </div>

        {itemsNumber > 0
          && <Summary
            itemsNumber={itemsNumber}
            className={styles.summary}
            sum={total}
            amountDiscount={amountDiscount}
            total={total}
          >
            <div className={styles.checkoutButton}>
              <CheckoutButton to={`${baseUrl}/checkout`}>
                {'Оформить'}
              </CheckoutButton>
            </div>
          </Summary>}
      </RowLayout>
    </React.Fragment>
  );
};

export default OrderList;
